import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from '../types';
import { authService } from '../services/auth.service';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  switchCity: (cityId: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const profile = await authService.getProfile();
    setUser(profile);
    if (profile.activeCityId) {
      localStorage.setItem('geoz_city_id', profile.activeCityId);
    } else if (profile.primaryCityId) {
      localStorage.setItem('geoz_city_id', profile.primaryCityId);
    }
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      if (authService.isAuthenticated()) {
        try {
          await refreshUser();
        } catch {
          authService.logout();
        }
      }
      setIsLoading(false);
    };
    loadUser();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const { user: loggedUser } = await authService.login(email, password);
    setUser(loggedUser);
    const activeCityId = loggedUser.activeCityId || loggedUser.primaryCityId || loggedUser.cityId;
    if (activeCityId) {
      localStorage.setItem('geoz_city_id', activeCityId);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('geoz_city_id');
    authService.logout();
  };

  const switchCity = async (cityId: string) => {
    const { user: switchedUser } = await authService.switchCity(cityId);
    setUser(switchedUser);
    if (switchedUser.activeCityId) {
      localStorage.setItem('geoz_city_id', switchedUser.activeCityId);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        switchCity,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
